import { PlusCircle } from 'lucide-react'
import { Controller, useFieldArray, useWatch, useFormContext } from 'react-hook-form'

import { MultiSelect } from '@/components/ui/MultiSelect'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'

import { BrandModel, ProductCategoryModel } from '@/types'
import { ProductModel } from '@/types/model/Product'

import { VariantsList } from './VariantsList'
import { productFieldsConfig } from './productFieldsConfig'

interface VariantsSectionProps {
  attributes: ProductModel.IAttribute[]
  attributesLoading: boolean
  categories: ProductCategoryModel.ProductCategory[]
  categoriesLoading: boolean
  brands: BrandModel.Brand[]
  brandsLoading: boolean
}

export const VariantsSection = ({ attributes, attributesLoading }: VariantsSectionProps) => {
  const { control, getValues } = useFormContext<ProductModel.ProductFormData>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variations',
  })

  const formValues = useWatch({ control })
  const { fields: configFields } = productFieldsConfig({
    formValues: formValues as ProductModel.ProductFormData,
  })

  const handleAddManual = () => {
    const productName = getValues('name') || ''
    append({
      name: '',
      description: '',
      summary: '',
      delivery_return_policy: '',
      purchase_cost: 0,
      regular_price: 0,
      attribute_value_ids: [],
      images: [],
      status: 'active',
      search_keywords: productName,
    })
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Configurable attributes</CardTitle>
        </CardHeader>
        <CardContent>
          <Controller
            control={control}
            name="attributes"
            rules={configFields.attributes.rules}
            render={({ field, fieldState: { error } }) => (
              <div className="space-y-1.5">
                <Label>Active attributes</Label>
                <MultiSelect
                  options={attributes.map((attr) => ({
                    label: attr.name,
                    value: attr._id,
                  }))}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="e.g. Color, Size, Material..."
                  disabled={attributesLoading}
                />
                <p className="text-sm text-text-2">Changes here update the configurable options in every variant below.</p>
                {error && <p className="text-sm text-error">{error.message}</p>}
              </div>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>Variants ({fields.length})</CardTitle>
            <CardDescription>Manage pricing, stock and details for each variant.</CardDescription>
          </div>
          <Button type="button" variant="secondary" onClick={handleAddManual}>
            <PlusCircle />
            Add variant
          </Button>
        </CardHeader>
        <CardContent>
          <VariantsList attributes={attributes} fields={fields} remove={remove} />
        </CardContent>
      </Card>
    </div>
  )
}
